import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import EmptyState from "@/components/base/EmptyState";
import Reveal from "@/components/base/Reveal";
import { news as newsData } from "@/mocks/news";
import type { NewsItem } from "@/types/content";
import { formatDate, formatDateShort } from "@/utils/format";

const news = newsData as NewsItem[];

export default function NewsDetail() {
  const { id } = useParams();
  const item = news.find((n) => n.id === id);

  if (!item) {
    return (
      <div className="w-full min-h-screen bg-background-50">
        <title>动态不存在 - 奥田咲最新动态</title>
        <SiteHeader />
        <main className="w-full px-4 md:px-8 pt-28 md:pt-36 pb-20">
          <div className="w-full max-w-3xl mx-auto rounded-lg border border-background-200 bg-background-50">
            <EmptyState
              icon="ri-newspaper-line"
              title="抱歉，该动态不存在或已删除"
              description="这条动态可能已被移除，或者链接输入有误。你可以返回动态列表，继续查看奥田咲的其他资讯。"
              actionLabel="返回动态列表"
              to="/news"
            />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = news
    .filter((n) => n.id !== item.id)
    .sort((a, b) => {
      const aScore = (a.category === item.category ? 1 : 0) + (a.date > b.date ? 1 : 0);
      const bScore = (b.category === item.category ? 1 : 0) + (b.date > a.date ? 1 : 0);
      return bScore - aScore;
    })
    .slice(0, 4);

  return (
    <div className="w-full min-h-screen bg-background-50">
      <title>{`${item.title} - 奥田咲最新动态`}</title>
      <meta
        name="description"
        content={`${item.summary} 了解奥田咲最新动态与影视工作进展。`}
      />
      <meta name="keywords" content={`奥田咲动态,奥田咲最新动态,${item.category}`} />

      <SiteHeader />

      <section className="w-full bg-primary-900 pt-24 md:pt-32 pb-10 md:pb-14">
        <div className="w-full px-4 md:px-8">
          <div className="w-full max-w-4xl mx-auto">
            <nav className="flex items-center flex-wrap gap-2 text-xs md:text-sm text-background-400">
              <Link to="/" className="hover:text-accent-300 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-background-500"></i>
              <Link to="/news" className="hover:text-accent-300 transition-colors">
                最新动态
              </Link>
              <i className="ri-arrow-right-s-line text-background-500"></i>
              <span className="text-background-200 line-clamp-1">{item.category}</span>
            </nav>

            <div className="mt-6">
              <span className="px-3 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-semibold">
                {item.category}
              </span>
              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-white leading-snug">
                {item.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-background-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  发布时间：{formatDate(item.date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-movie-2-line"></i>
                  奥田咲动态
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="w-full max-w-4xl mx-auto">
          <Reveal>
            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={item.cover}
                alt={`${item.title} - 奥田咲动态详情配图`}
                title={`${item.title} 奥田咲动态`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </Reveal>

          <Reveal delay={80}>
            <article className="mt-10 space-y-6">
              {item.content.map((paragraph, idx) => (
                <p
                  key={`${item.id}-p-${idx}`}
                  className="text-sm md:text-base text-foreground-700 leading-9"
                >
                  {paragraph}
                </p>
              ))}
            </article>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-10 pt-8 border-t border-background-200 flex flex-wrap items-center justify-between gap-4">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回动态列表
              </Link>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">分享：</span>
                {["ri-twitter-x-line", "ri-instagram-line", "ri-weibo-line"].map((icon) => (
                  <span
                    key={icon}
                    className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800 transition-colors"
                  >
                    <i className={`${icon} text-lg`}></i>
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="mt-16">
            <div className="flex items-end justify-between gap-4 mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                相关文章推荐
              </h2>
              <Link
                to="/news"
                className="text-sm text-primary-700 hover:text-accent-600 transition-colors whitespace-nowrap"
              >
                更多动态
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {related.map((r, idx) => (
                <Reveal key={r.id} delay={idx * 50}>
                  <Link
                    to={`/news/${r.id}`}
                    className="group flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer h-full"
                  >
                    <div className="w-24 shrink-0 aspect-square rounded-md overflow-hidden bg-background-200">
                      <img
                        src={r.cover}
                        alt={`${r.title} - 奥田咲相关动态`}
                        title={`${r.title} 奥田咲动态`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs text-foreground-500">{formatDateShort(r.date)}</span>
                      <h3 className="mt-1.5 font-heading text-sm md:text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                        {r.title}
                      </h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}